/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon, ActionIconDropdown, DetailStack, Dropdown, HStack, ICON_EYE_OUTLINE, Popover, Text,
  SimpleTable,
} from '@doveit/bricks';
import { Task } from '../../../providers/api/dtos';
import TaskPriorityBadge from '../../components/task-priority-badge/TaskPriorityBadge';
import { ReferenceType, TaskStatus } from '../../../domain/types';
import { CELL_DATE_WIDTH } from '../../../constants';
import DateViewer from '../../../components/date-viewer/DateViewer';
import * as styles from './ViewSerpTaskTableRow.style';
import { goToDetailPage } from '../../../utils/navigate/utils';
import TaskPreview from '../../components/task-preview/TaskPreview';
import UpdateTaskStatus from '../update-task-status/UpdateTaskStatus';
import usePropertyPreview from '../../../property/hooks/use-property-preview/usePropertyPreview';
import PropertyPreview from '../../../property/components/property-preview/PropertyPreview';
import UpsertTaskAction from '../upsert-task-action/UpsertTaskAction';
import { contentEditorUsers } from '../../constants';
import UserAvatar from '../../../user/components/user-avatar/UserAvatar';
import UserPopoverPreview from '../../../user/components/user-popover-preview/UserPopoverPreview';
import useAssignmentByPropertyId from '../../../property/hooks/use-assignment-by-property-id/useAssignmentByPropertyId';
import useProspect from '../../../prospect/hooks/use-prospect/useProspect';
import useLead from '../../../lead/hooks/use-lead/useLead';

export interface ViewSerpTaskTableRowProps extends React.AriaAttributes {
  task: Task,
  onTaskUpdated?: (task: Task) => void,
}

const ViewSerpTaskTableRow: React.FC<ViewSerpTaskTableRowProps> = ({
  task,
  onTaskUpdated,
  ...rest
}) => {
  /**
   * @todo handle other references
   */
  const { data: property } = usePropertyPreview(task.reference?.type === ReferenceType.PROPERTY
    ? parseInt(task.reference.id, 10)
    : undefined);

  const { data: assignment } = useAssignmentByPropertyId(property?.id);
  const { data: prospect } = useProspect(assignment?.prospectId);
  const { data: lead } = useLead(prospect?.leadId);

  /**
  * TODO: https://doveit.atlassian.net/browse/DOVE-14128
  */
  const assignee = React.useMemo(
    () => (task?.assignee ? contentEditorUsers.find((u) => u.email === task.assignee) : undefined),
    [task],
  );

  const goToTaskPage = React.useCallback(() => {
    goToDetailPage(task.id!, ReferenceType.TASK);
  }, [task.id]);

  return (
    <SimpleTable.Row
      aria-label="Informazioni attività"
      {...rest}
    >
      <SimpleTable.Cell
        aria-label="Preview"
        width="10rem"
      >
        <TaskPreview task={task} />
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Priorità">
        <TaskPriorityBadge
          task={task}
          size="XS"
          showIcon
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Stato"
        width="6.5rem"
      >
        <UpdateTaskStatus
          task={task}
          onSuccess={onTaskUpdated}
          size="XS"
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Preview referenza"
        width="10rem"
      >
        {property && <PropertyPreview property={property} isPersonal={!!lead?.originAgentId} />}
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Assegnatario dell'attività">
        <UserPopoverPreview
          user={assignee}
          trigger={<UserAvatar size="S" />}
          namePlaceholder="Non assegnato"
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell aria-label="Data di creazione">
        <DetailStack
          label="Aggiunta"
          aria-label="Data di creazione attività"
          width={CELL_DATE_WIDTH}
        >
          <DateViewer
            stringDate={task.createdAt!}
            checkIfToday
          />
        </DetailStack>
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Descrizione"
        withDivider
      >
        {task.description && (
          <Popover
            trigger={(
              <Text.Caption css={styles.description}>
                {task.description}
              </Text.Caption>
            )}
            hasArrow
          >
            <div css={styles.descriptionPopoverWrapper}>
              <Text.Body>
                {task.description}
              </Text.Body>
            </div>
          </Popover>
        )}
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Azioni"
        align="right"
      >
        <HStack gap={75} wrap="nowrap">
          <ActionIcon
            aria-label="Visualizza attività"
            label="Visualizza"
            size="S"
            icon={{ path: ICON_EYE_OUTLINE }}
            onClick={goToTaskPage}
          />

          {task?.status !== TaskStatus.DONE && (
            <ActionIconDropdown
              aria-label="Visualizza azioni secondarie"
              label="Azioni"
              size="S"
              emphasis="low"
            >
              <UpsertTaskAction
                onSuccess={onTaskUpdated}
                task={task}
              >
                {({ upsert, label }) => (
                  <Dropdown.Option
                    label={label}
                    aria-label={`${label} attività`}
                    onClick={upsert}
                  />
                )}
              </UpsertTaskAction>
            </ActionIconDropdown>
          )}
        </HStack>
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default ViewSerpTaskTableRow;
